import { useCallback, useRef } from 'react'
import type { ReactNode } from 'react'
import { historicalFocusDate } from '@/lib/exchange-time'
import type { WindowConfig, LinkMode } from '../types'
import type { ComponentType } from '@/types/schemas/workspace'
import { COMPONENT_META } from './add-panel-menu-constants'
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip'
import { TickerSearch } from '@/components/ticker/ticker-search'
import { useTypeToTickerSearch } from '@/components/ticker/use-type-to-ticker-search'
import { PanelActionButtons } from './panel-actions'
import { PanelMoreMenu } from './panel-more-menu'
import { RolloutFlag } from './rollout-gate'
import { PANEL_ROLLOUT_FLAGS } from '../panel-rollout'
import { ChartLocalDatePicker } from './chart-local-date-picker'
import { useFeatureFlag } from '@/services/analytics/use-feature-flag'
import { FLAGS } from '@/services/analytics/feature-flags'
import { linkedFocusDate, resolveFocusBroadcast } from './focus-date-sources'
import { ChartHistoryWindowControl } from '@/features/charts/chart-window-control'
import { useDevice } from '@/context/device-hooks'
import { getEffectiveTradingDay } from '@/lib/trading-days'
import { formatIsoDate } from '@/components/grid/formatters'
import { NotebookModeSelect } from '@/features/notebook/components/notebook-mode-select'
import { useLayoutPanel } from '@/workspace/use-layout-panel'
import {
  STAT_CARD_COMPONENT_TYPES,
  isStatCardsVisible,
} from '@/features/ticker-stats/components/stat-card-slots'
import {
  cellCountOf,
  DEFAULT_CHART_LAYOUT,
  type MultiChartLayout,
} from '@/types/schemas/chart-layout'
import type { ChartSettings } from '@edge/charts'
import { textPx } from '@/features/theme-editor/text-scale'

// ─── Helpers ───

/** Stops both mousedown propagation and native drag on interactive elements. */
function stopDrag(e: React.MouseEvent | React.DragEvent) {
  e.stopPropagation()
}

function preventDragStart(e: React.DragEvent) {
  e.preventDefault()
  e.stopPropagation()
}

// Component-type icons and title labels come from COMPONENT_META — the
// single type→icon/label source shared with the add-panel menu and the
// pane tab strip, so a panel is named and iconed identically everywhere.

/** Component types that show the ticker search pill. Others show children directly. */
const TICKER_SEARCH_TYPES = new Set<string>([
  'chart',
  'key-stats',
  'news',
  'ticker-stats',
  'history-chart',
  'history-news',
  'backtest-trade-chart',
])

/** Component types that show the date next to the ticker pill. */
const DATE_DISPLAY_TYPES = new Set<string>([
  'chart',
  'history-chart',
  'history-news',
  'backtest-trade-chart',
])

/** Component types that show the calendar date picker. */
const DATE_PICKER_TYPES = new Set<string>(['chart', 'scanner', 'multi-chart', 'watchlist'])

/**
 * Chart-family panels whose header pill hosts the full chart chrome: in-pill
 * calendar segment, bar replay, screenshot. `history-chart` is the same
 * ChartGrid surface as `chart` (only its data binding differs), so it gets
 * identical header treatment.
 */
const CHART_HEADER_TYPES = new Set<string>(['chart', 'history-chart', 'backtest-trade-chart'])

/** Component types that render children as a plain title (no pill background). */
const TITLE_ONLY_TYPES = new Set<string>([
  'multi-scanner',
  'calendar',
  'notebook',
  'script-editor',
  'history-records',
  'history-stats',
  'history-comparison',
  'history-time-series',
  'history-scatter',
  'history-bar',
  'history-bar-category',
  'history-donut-category',
  'history-intraday',
  'history-time-distribution',
  'history-heatmap',
  'history-calendar',
  'history-multi-chart',
  'history-sankey',
  'tags-explorer',
  // Market Overwatch names itself: it is one market-wide reading, not a view of
  // a ticker, so it takes the plain title and puts its market selector beside
  // it in the selector's own pill (the notebook shape below).
  'market-overwatch',
  // NB: price-alerts / alert-windows are intentionally NOT here either: like
  // the Scanner, their selector (status filter / alert window) fills the
  // title slot inside the non-ticker pill (renderToolbarExtras).
  // NB: theme-tracker is intentionally NOT here — like the heatmap it shows no
  // title; its header dropdowns (universe + column) occupy the title slot via
  // renderToolbarExtras (the non-ticker pill branch).
])

/** Panel types whose more menu offers a reset to their default, sent to the panel's toolbar handler. */
const MENU_RESET: Partial<Record<ComponentType, { actionId: string; label: string }>> = {
  'alert-windows': { actionId: 'reset-window', label: 'Reset to default window' },
}

/**
 * Whether a component type renders a plain title label instead of the ticker
 * pill. All backtest-* panels qualify by prefix — they have no ticker context —
 * so newly added backtest panel types get a title without registering here.
 */
function isTitleOnlyType(componentType: string): boolean {
  return (
    TITLE_ONLY_TYPES.has(componentType) ||
    componentType.startsWith('backtest-') ||
    componentType.startsWith('journal-')
  )
}

// NB: the toolbar show/hide "eye" toggle has been removed for every panel
// type. Chart chrome (floating chips / DrawingBar / settings gear) is now
// always available on hover. The `toolbarVisible` window-config field it wrote
// is gone too; hosts that need a chrome-less chart pass ChartPanel's explicit
// `toolbarVisible={false}` prop instead.

// ─── Panel Header ───

interface PanelHeaderProps {
  panelId: string
  windowConfig: WindowConfig
  symbols: string[]
  onSymbolChange: (symbolIndex: number) => void
  onSelectTicker?: (ticker: string, focusDate?: string | null) => void
  onLinkModeChange: (mode: LinkMode) => void
  onDelete: () => void
  onExport?: () => void
  onDuplicate?: () => void
  /** Relocate this panel (panel menu Move). Undefined hides the item. */
  onMove?: () => void
  /** Label for the panel menu's Move item ("Move tab" in a tabbed pane). */
  moveLabel?: string
  onAction?: (actionId: string, anchor?: { x: number; y: number }) => void
  /** Override the default action list. Array of action IDs (e.g. ['aggregates', 'filters', 'link']). */
  actionOverrides?: string[]
  /** Badge counts to display on action buttons (e.g. { filters: 7 }). */
  actionBadges?: Record<string, number>
  /** When true, the layout is locked — hide destructive actions in the more menu. */
  locked?: boolean
  /** Historical focus date (YYYY-MM-DD) — shown instead of today when set. */
  focusDate?: string | null
  /** Callback to set the focus date (YYYY-MM-DD or null to reset). */
  onFocusDate?: (date: string | null) => void
  /** Callback to reset the focus date (return to live). */
  onResetFocusDate?: () => void
  /** Which component type initiated the current focus date. */
  focusDateSource?: string | null
  /** When true, the ticker filter is cleared and the pill shows "All". */
  tickerCleared?: boolean
  /** Callback to clear the ticker filter. */
  onClearTicker?: () => void
  /** Extra content rendered inside the ticker pill (e.g. period dropdown) */
  children?: ReactNode
  /** Extra content rendered on the right side of the header, before the action button block. */
  rightExtras?: ReactNode
  /** Callback to toggle headerVisible on the panel's windowConfig. */
  onToggleHeaderVisible?: () => void
  /** Open the chat drawer with this panel already added as a mention (the ⋯ menu's "Ask AI" item). */
  onAskAI?: () => void
}

/**
 * Panel header — renders inside the MosaicWindow toolbar.
 *
 * The toolbar area is the mosaic drag source. Interactive elements (buttons,
 * inputs, dropdowns) block panel drag by being `draggable=true` themselves and
 * preventDefault-ing their own dragstart — this claims them as the drag
 * source (since HTML5 DnD picks the nearest draggable=true ancestor) and then
 * immediately cancels the drag. Setting draggable={true} alone does NOT work
 * in Chrome: an ancestor draggable=true still wins. Pattern carried over from
 * the old frontend's preventParentDragHandle.
 * The empty background space between elements remains draggable.
 */
export function PanelHeader({
  panelId,
  windowConfig,
  symbols,
  onSymbolChange,
  onSelectTicker,
  onLinkModeChange,
  onDelete,
  onExport,
  onDuplicate,
  onMove,
  moveLabel,
  onAction,
  actionOverrides,
  actionBadges,
  locked,
  focusDate,
  onResetFocusDate,
  focusDateSource,
  tickerCleared,
  onClearTicker,
  children,
  rightExtras,
  onToggleHeaderVisible,
  onAskAI,
}: PanelHeaderProps) {
  const { isMobile } = useDevice()
  const currentTicker = symbols[windowConfig.symbolIndex] ?? symbols[0] ?? '—'

  // Only chart and scanner schemas carry focusDate; other types leave it undefined.
  const { panelState, updatePanel } = useLayoutPanel<{
    focusDate?: string | null
    drillDate?: string | null
    layout?: MultiChartLayout
    settings?: Partial<ChartSettings>
    showCards?: boolean
  }>(panelId)
  const panelLocalFocusDate = panelState?.focusDate ?? null
  // A drill-down is open and anchored to a day: the header date picker shows
  // that day (yellow), but Reset is hidden — Exit Drill Down (inside the
  // overlay) is the way out, not a focus-date reset.
  const isDrilling = panelState?.drillDate != null

  // Bar replay (V1) is offered only on a chart panel that currently shows a
  // SINGLE, non-drilled chart — multi-cell grids and the scanner get no entry.
  const cellCount = cellCountOf(panelState?.layout ?? DEFAULT_CHART_LAYOUT)
  const canBarReplay =
    CHART_HEADER_TYPES.has(windowConfig.componentType) && cellCount === 1 && !isDrilling

  // Scanner minute snapshot: a New York time field in the header calendar.
  const minuteSnapshotFlag = useFeatureFlag(FLAGS.SCANNER_MINUTE_SNAPSHOT)
  const minuteSnapshot = minuteSnapshotFlag && windowConfig.componentType === 'scanner'

  // Stat-card row (Ticker Stats / Market Stats): the more-menu is its only
  // switch, so the header owns the toggle and writes the panel's showCards.
  const hasStatCards = STAT_CARD_COMPONENT_TYPES.has(windowConfig.componentType)
  const cardsVisible = isStatCardsVisible(windowConfig.componentType, panelState?.showCards)

  // Bridge shared TickerSearch (symbol string) → panel's index-based or new-ticker callbacks
  const handleTickerSearchSelect = useCallback(
    (symbol: string) => {
      const idx = symbols.findIndex((s) => s.toUpperCase() === symbol.toUpperCase())
      if (idx !== -1) {
        onSymbolChange(idx)
      } else if (onSelectTicker) {
        onSelectTicker(symbol)
      }
    },
    [symbols, onSymbolChange, onSelectTicker],
  )

  // ThinkorSwim-style `-TICKER` prefix: chart-family panels only (the same set
  // that gets the in-pill calendar segment). Reads/writes the SAME
  // `panelState.settings.priceScaleInvert` that chart-panel.tsx's own
  // Settings-modal path writes — no parallel state.
  const isChartHeader = CHART_HEADER_TYPES.has(windowConfig.componentType)

  // Type-to-search: with the pointer over the panel, a bare letter opens this
  // header's ticker search seeded with it. Chart-family panels are left out
  // because ChartGrid already owns the gesture (and its chart-only guards);
  // claiming it here too would open two searches. Mobile shows no search.
  const headerRef = useRef<HTMLDivElement>(null)
  useTypeToTickerSearch({
    enabled: !isMobile && TICKER_SEARCH_TYPES.has(windowConfig.componentType) && !isChartHeader,
    anchorRef: headerRef,
  })
  const invertScaleOn = isChartHeader && panelState?.settings?.priceScaleInvert === true
  const handleInvertPrefixChange = useCallback(
    (inverted: boolean) => {
      if ((panelState?.settings?.priceScaleInvert ?? false) === inverted) return
      updatePanel({ settings: { ...panelState?.settings, priceScaleInvert: inverted } })
    },
    [panelState?.settings, updatePanel],
  )

  // Panel-local wins over broadcast. Broadcast displays on the source panel
  // and on chart panels (chart always reflects broadcasts), except a locked
  // chart: it holds its own day, so the broadcast never reaches its body and
  // nothing here may show it either.
  const linkedBroadcast = linkedFocusDate(windowConfig, focusDate)
  const isAffectedByFocus =
    windowConfig.componentType === focusDateSource || windowConfig.componentType === 'chart'
  const broadcastFallback = linkedBroadcast && isAffectedByFocus ? linkedBroadcast : null
  // What the date pill may show. Separate from `isAffectedByFocus` above (which
  // governs the Reset control): a source is allowed to drive only certain panel
  // types, and the pill must not advertise a date this panel's body ignores.
  const pillBroadcastDate = resolveFocusBroadcast(
    windowConfig.componentType,
    linkedBroadcast,
    focusDateSource,
  )
  // historicalFocusDate: an explicit TODAY (the calendar's Today button) is
  // live — the Reset control must disappear with the border, not linger.
  const effectiveFocusDate = historicalFocusDate(panelLocalFocusDate ?? broadcastFallback)

  // Visible hours shows exactly when the chart body applies the window
  // (chart-grid's isHistoryMode). The dashboard chart treats today as live; the
  // History page's chart anchors today (ChartGridConfig.anchorToday), so any
  // date it shows, today included, counts as history there.
  const showVisibleHours =
    windowConfig.componentType === 'chart'
      ? effectiveFocusDate != null
      : windowConfig.componentType === 'history-chart' &&
        (panelLocalFocusDate ?? pillBroadcastDate) != null

  // Reset clears whichever channel is currently showing: panel-local if set
  // (no sibling effect), otherwise the broadcast (which was shared anyway).
  const handleReset = useCallback(() => {
    if (panelLocalFocusDate != null) {
      updatePanel({ focusDate: null })
    } else {
      onResetFocusDate?.()
    }
  }, [panelLocalFocusDate, updatePanel, onResetFocusDate])

  const reset = MENU_RESET[windowConfig.componentType]
  const resetFlag = PANEL_ROLLOUT_FLAGS[windowConfig.componentType]
  const menu = (resetOn: boolean) => (
    <PanelMoreMenu
      onDelete={onDelete}
      onExport={onExport}
      onDuplicate={onDuplicate}
      onMove={onMove}
      moveLabel={moveLabel}
      locked={locked}
      onToggleHeaderVisible={onToggleHeaderVisible}
      headerVisible={windowConfig.headerVisible ?? true}
      // Chart panels register a 'screenshot' action handler; the ⋯ menu
      // is its entry point (moved out of the chart's own toolbar).
      onScreenshot={
        CHART_HEADER_TYPES.has(windowConfig.componentType)
          ? () => onAction?.('screenshot')
          : undefined
      }
      // Bar replay — single non-drilled chart panels only (see canBarReplay).
      onBarReplay={canBarReplay ? () => onAction?.('bar-replay') : undefined}
      onToggleCards={hasStatCards ? () => updatePanel({ showCards: !cardsVisible }) : undefined}
      cardsVisible={cardsVisible}
      onAskAI={onAskAI}
      onReset={reset && resetOn ? () => onAction?.(reset.actionId) : undefined}
      resetLabel={reset?.label}
    />
  )

  return (
    <div
      ref={headerRef}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        padding: '0 8px',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
      }}
    >
      {/* Left side — always visible, never shrinks */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
        {/* Component-type icon (draggable — part of toolbar bg) */}
        {(() => {
          const componentMeta = COMPONENT_META[windowConfig.componentType]
          const TypeIcon = componentMeta?.icon
          const typeLabel = componentMeta?.label ?? windowConfig.componentType
          const icon = (
            <span
              className="flex items-center text-muted-foreground"
              style={{ marginRight: 4 }}
              data-testid="panel-type-icon"
              aria-label={typeLabel}
            >
              {TypeIcon ? (
                <TypeIcon size={15} strokeWidth={1.8} />
              ) : (
                <svg
                  width={15}
                  height={15}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.8}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                </svg>
              )}
            </span>
          )
          return (
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>{icon}</TooltipTrigger>
                <TooltipContent side="bottom">{typeLabel}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )
        })()}

        {TICKER_SEARCH_TYPES.has(windowConfig.componentType) ? (
          <>
            {/* Unified pill: ticker search (+ period dropdown when toolbar hidden).
                No pill-wide hover — each segment highlights on its OWN hover, so
                only the icon under the cursor lights up (not the whole pill). */}
            <div
              onMouseDown={stopDrag}
              onDragStart={preventDragStart}
              draggable={true}
              className="panel-header-pill flex h-6 items-center rounded bg-background/55"
            >
              {isMobile ? (
                /* Mobile: plain ticker label (search lives in TopBar) */
                <span
                  className="flex items-center text-muted-foreground"
                  style={{ fontSize: textPx(12), fontWeight: 500, padding: '0 8px', height: 24 }}
                >
                  {tickerCleared ? 'All' : currentTicker}
                </span>
              ) : (
                <TickerSearch
                  // A cleared chart shows no ticker (the placeholder); a cleared
                  // feed shows every ticker.
                  value={
                    tickerCleared
                      ? isChartHeader
                        ? undefined
                        : 'All'
                      : invertScaleOn
                        ? `-${currentTicker}`
                        : currentTicker
                  }
                  onSelect={handleTickerSearchSelect}
                  invertPrefix={isChartHeader}
                  onInvertPrefixChange={isChartHeader ? handleInvertPrefixChange : undefined}
                  showClear={!tickerCleared && !!onClearTicker}
                  onClear={onClearTicker}
                  companionLabel={
                    windowConfig.componentType
                      ? `${windowConfig.componentType}-ticker-search`
                      : 'ticker-search'
                  }
                  // h-7 matches the other header segments; the inline
                  // TickerSearch supplies its own hover:bg-hover (when not
                  // editing), so we no longer suppress it with bg-transparent.
                  className="h-7 border-0 bg-transparent px-2 text-[12px]"
                />
              )}
              {/* Children render inside the pill (e.g. divider + period dropdown) */}
              {children}
              {/* Calendar — chart panels fold it into the pill as the rightmost
                  segment, matching the other header controls. */}
              {CHART_HEADER_TYPES.has(windowConfig.componentType) && (
                <>
                  <span
                    style={{
                      width: 1,
                      alignSelf: 'stretch',
                      background: 'hsl(var(--border))',
                      flexShrink: 0,
                    }}
                  />
                  <ChartLocalDatePicker
                    panelId={panelId}
                    groupFocusDate={pillBroadcastDate}
                    // The calendar owns replay date + time on chart panels that
                    // can host one (same gate as the ⋯-menu entry).
                    replayEnabled={canBarReplay}
                    onAction={onAction}
                  />
                  {/* Regular charts in history mode get the same visible-hours
                      control the history grid has, bound to the global
                      "history time range" preference. Live charts hide it. */}
                  {showVisibleHours && <ChartHistoryWindowControl panelId={panelId} />}
                </>
              )}
            </div>

            {/* Calendar date picker — non-chart panels keep it beside the pill */}
            {DATE_PICKER_TYPES.has(windowConfig.componentType) &&
              !CHART_HEADER_TYPES.has(windowConfig.componentType) && (
                <ChartLocalDatePicker
                  panelId={panelId}
                  groupFocusDate={pillBroadcastDate}
                  minuteSnapshot={minuteSnapshot}
                />
              )}
            {/* Plain date display — non-chart panels that still show dates */}
            {DATE_DISPLAY_TYPES.has(windowConfig.componentType) &&
              !CHART_HEADER_TYPES.has(windowConfig.componentType) && (
                <span
                  className={
                    focusDate && !windowConfig.componentType.startsWith('history-')
                      ? 'text-yellow-500'
                      : 'text-muted-foreground'
                  }
                  style={{ fontSize: textPx(13) }}
                >
                  {focusDate ?? formatIsoDate(getEffectiveTradingDay())}
                </span>
              )}
          </>
        ) : isTitleOnlyType(windowConfig.componentType) ? (
          /* Title-only panels: plain text label, no pill background */
          <div
            onMouseDown={stopDrag}
            onDragStart={preventDragStart}
            draggable={true}
            style={{ display: 'flex', alignItems: 'center' }}
          >
            {windowConfig.componentType === 'notebook' ? (
              /* Notebook: a mode selector in a pill (the ScanNameSelector pattern),
                 mirroring the rail tabs so navigation survives a collapsed rail. */
              <div className="panel-header-pill flex h-6 items-center rounded bg-background/55">
                <NotebookModeSelect panelId={panelId} />
              </div>
            ) : (
              COMPONENT_META[windowConfig.componentType]?.label && (
                <span
                  className="text-xs font-medium text-muted-foreground"
                  // marginRight gives the header dropdowns a little breathing room
                  // from the panel title (no-op when a panel has no left controls).
                  style={{ paddingLeft: 2, marginRight: 8 }}
                >
                  {COMPONENT_META[windowConfig.componentType].label}
                </span>
              )
            )}
            {children}
          </div>
        ) : (
          /* Non-ticker panels: scanner-style pill with the date picker folded
             IN as the rightmost segment (separator + calendar), sharing the
             pill's background group — same as the chart header, instead of
             floating beside the pill. */
          <div
            onMouseDown={stopDrag}
            onDragStart={preventDragStart}
            draggable={true}
            className="panel-header-pill flex h-6 items-center rounded bg-background/55"
          >
            {/* No pill-wide hover — each segment (scan/sort/watchlist selector)
                highlights on its OWN hover, so only the control under the cursor
                lights up (not the whole pill). Parity with the chart pill above. */}
            {children}
            {DATE_PICKER_TYPES.has(windowConfig.componentType) && (
              <>
                <span
                  style={{
                    width: 1,
                    alignSelf: 'stretch',
                    background: 'hsl(var(--border))',
                    flexShrink: 0,
                  }}
                />
                <ChartLocalDatePicker
                  panelId={panelId}
                  groupFocusDate={pillBroadcastDate}
                  minuteSnapshot={minuteSnapshot}
                />
              </>
            )}
          </div>
        )}
      </div>
      {/* Right side — action icons block drag individually */}
      <div
        onMouseDown={stopDrag}
        onDragStart={preventDragStart}
        draggable={true}
        style={{ display: 'flex', alignItems: 'center', flexShrink: 1, minWidth: 0, marginLeft: 8 }}
      >
        {/* Collapse region - ONE right-anchored clip box for everything except
            the always-visible more-menu. justify-end + overflow hidden means a
            narrowing panel consumes content from this box's LEFT edge, so the
            uniform priority is: left header content always shows, then the
            rightmost controls (nearest the more-menu) survive longest, and
            whatever sits leftmost here (count label, refresh timer, Reset,
            then the leftmost action icons) gives way first. */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 2,
            overflow: 'hidden',
            flexShrink: 1,
            minWidth: 0,
          }}
        >
          {/* rightExtras (count label / refresh timer / pager) render as DIRECT
              children of the clip region - no shrinkable wrapper. Extras that
              opt into collapsing carry their own min-w-0/shrink (the count
              label, the pager's status text); everything else keeps its
              natural width, so a narrowing panel CLIPS items one by one from
              the left instead of squashing them into each other. */}
          {rightExtras}
          <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
            {/* Reset focus date — show on the initiating panel + chart panels (always affected).
              Skip history panels, the tag explorer AND every backtest report
              panel (the Executions grid broadcasts the selected trade's day,
              the Trade Chart follows it): all are inherently historical
              surfaces, so "reset to live" is meaningless there.
              Hidden while a drill-down is open: Exit Drill Down owns that exit. */}
            {effectiveFocusDate &&
              !isDrilling &&
              !windowConfig.componentType.startsWith('history-') &&
              !windowConfig.componentType.startsWith('backtest-') &&
              windowConfig.componentType !== 'tags-explorer' && (
                <button
                  title="Reset to live"
                  data-companion-label="reset-date"
                  onClick={handleReset}
                  onMouseDown={stopDrag}
                  onDragStart={preventDragStart}
                  draggable={true}
                  className="mr-1 rounded border border-yellow-500/50 px-2 py-0.5 text-xs font-medium text-yellow-500 hover:bg-yellow-500/10 transition-colors"
                >
                  Reset
                </button>
              )}

            <PanelActionButtons
              componentType={windowConfig.componentType}
              linkMode={windowConfig.linkMode ?? 'onChange'}
              onLinkModeChange={onLinkModeChange}
              onAction={onAction}
              actionOverrides={actionOverrides}
              actionBadges={actionBadges}
            />
          </div>
        </div>

        {/* Three-dots context menu — always visible */}
        <div style={{ flexShrink: 0 }}>
          {reset && resetFlag ? <RolloutFlag flag={resetFlag}>{menu}</RolloutFlag> : menu(true)}
        </div>
      </div>
    </div>
  )
}
